import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUnleashContext } from '@unleash/proxy-client-react'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
// eslint-disable-next-line import-x/no-unresolved
import { useCookies } from 'react-cookie'
import { useLocation, useNavigate } from 'react-router'

import { IMPERSONATION_BANNER_CLOSED_KEY } from '@/components/ImpersonatingBanner/constants'
import { useAppMessages } from '@/hooks/useAppMessages'
import { usePreviousValue } from '@/hooks/usePreviousValue'
import { useStringUrlQueryState } from '@/hooks/useUrlQueryState'
import { Heap } from '@/monitoring/heap'
import { NewRelic } from '@/monitoring/newrelic'
import { PATHS } from '@/routes/PATHS'
import { authService, type UserAttributes } from '@/services/api/auth.service'
import { usersService } from '@/services/api/users.service'
import {
  usePremiumFeaturesAccountCreatedAt,
  usePremiumFeaturesAccountType,
} from '@/stores/permissions/premiumFeatures'
import {
  IMPERSONATION_COOKIE_NAME,
  IMPERSONATION_COOKIE_SETTINGS,
  MARQII_COGNITO_CLIENT_ID,
  MARQII_COGNITO_COOKIE_SETTINGS,
} from '@/utils/constants/oauth'

export interface CognitoAuthContextType {
  userId: string
  userAttributes?: UserAttributes
  accountId: string
  accessToken: string
  isAuthenticatedPreviousValue: boolean
  isAuthenticated: boolean
  isFirstTimeLogin: boolean
  isMarqiiUser: boolean
  loading: boolean
  impersonation: {
    isImpersonating: boolean
  }
  login: (input: { email: string; password: string }) => void
  logout: () => void
  updateUserAttributes: (data: Partial<UserAttributes>) => Promise<void>
  changePassword: (input: { oldPassword: string; newPassword: string }) => Promise<void>
  forgotPassword: (input: { email: string }) => Promise<void>
  resetPassword: (input: { email: string; code: string; password: string }) => Promise<void>
  changeTemporaryPassword: (input: {
    newPassword: string
    optInMarketing?: boolean
  }) => Promise<void>
  getAccessToken: () => Promise<string | undefined>
}

const CognitoAuthContext = createContext<CognitoAuthContextType>({} as CognitoAuthContextType)

function useAuthSession() {
  return useQuery({
    queryKey: ['auth-session'],
    queryFn: () => authService.fetchAuthSession(),
    enabled: true,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

function useAuthUserAttributes() {
  return useQuery({
    queryKey: ['auth-user-attributes'],
    queryFn: () => authService.fetchUserAttributes() as unknown as UserAttributes,
    enabled: true,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

function useAuthLogin() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const message = useAppMessages()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.signIn({ username: email, password }),
    onSuccess: async (data) => {
      if (data.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        await navigate(PATHS.newPasswordPage, { replace: true })
      } else {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['auth-session'] }),
          queryClient.invalidateQueries({ queryKey: ['auth-user-attributes'] }),
        ])
      }
    },
    onError: (error) => {
      console.error('Error on sign in:', error)
      message.error(error.message)
    },
  })
}

function useAuthLogout() {
  const message = useAppMessages()
  const [_, __, removeCookie] = useCookies([IMPERSONATION_COOKIE_NAME, MARQII_COGNITO_CLIENT_ID])
  return useMutation({
    mutationFn: async () => {
      removeCookie(IMPERSONATION_COOKIE_NAME, IMPERSONATION_COOKIE_SETTINGS)
      removeCookie(MARQII_COGNITO_CLIENT_ID, MARQII_COGNITO_COOKIE_SETTINGS)
      await authService.signOut()
      localStorage.removeItem(IMPERSONATION_BANNER_CLOSED_KEY)
    },
    onSuccess: () => {
      window.location.reload()
    },
    onError: (error) => {
      console.error('Error on sign out:', error)
      message.error(error.message)
    },
    onSettled: () => {
      Heap.clear()
      NewRelic.clear()
    },
  })
}

function useAuthForgotPassword() {
  const message = useAppMessages()
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      await authService.resetPassword({ username: email })
    },
    onError: (error) => {
      console.error('Error on forgot:', error)
      message.error(error.message)
    },
  })
}

function useAuthResetPassword() {
  const message = useAppMessages()
  return useMutation({
    mutationFn: async ({
      email,
      code,
      password,
    }: {
      email: string
      code: string
      password: string
    }) => {
      await authService.confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      })
    },
    onError: (error) => {
      console.error('Error on reset:', error)
      message.error(error.message)
    },
  })
}

function useAuthChangeTemporaryPassword() {
  const queryClient = useQueryClient()
  const message = useAppMessages()
  return useMutation({
    mutationFn: async ({
      newPassword,
      optInMarketing,
    }: {
      newPassword: string
      optInMarketing?: boolean
    }) => {
      await authService.confirmSignIn({
        challengeResponse: newPassword,
        options: { userAttributes: optInMarketing ? { 'custom:optInMarketing': '1' } : undefined },
      })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth-session'] }),
        queryClient.invalidateQueries({ queryKey: ['auth-user-attributes'] }),
      ])
    },
    onError: (error) => {
      console.error('Error on change temporary password:', error)
      message.error(error.message)
    },
  })
}

function useAuthChangePassword() {
  return useMutation({
    mutationFn: async ({
      oldPassword,
      newPassword,
    }: {
      oldPassword: string
      newPassword: string
    }) => {
      await authService.updatePassword({ oldPassword, newPassword })
    },
    onError: (error) => {
      console.error('Error on change password:', error)
    },
  })
}

function useAuthUpdateUserAttributes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userAttributes: Partial<UserAttributes>) => {
      await usersService.updateUserAttributes({
        name: userAttributes.name,
        email: userAttributes.email,
        givenName: userAttributes.given_name,
        familyName: userAttributes.family_name,
        emailVerified: userAttributes.email_verified,
        sub: userAttributes.sub,
        updatedAt: userAttributes.updated_at,
        'custom:optInMarketing': userAttributes['custom:optInMarketing'],
        'custom:tenantId': userAttributes['custom:tenantId'],
      })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth-session'] }),
        queryClient.invalidateQueries({ queryKey: ['auth-user-attributes'] }),
      ])
    },
    onError: (error) => {
      console.error('Error on update user attributes', error)
    },
  })
}

export function CognitoAuthProvider({ children }: React.PropsWithChildren): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const updateContext = useUnleashContext()
  const accountCreatedAt = usePremiumFeaturesAccountCreatedAt()
  const accountType = usePremiumFeaturesAccountType()
  const [redirectUri] = useStringUrlQueryState('redirect_uri')
  const session = useAuthSession()
  const userAttributes = useAuthUserAttributes()
  const login = useAuthLogin()
  const logout = useAuthLogout()
  const forgotPassword = useAuthForgotPassword()
  const resetPassword = useAuthResetPassword()
  const changeTemporaryPassword = useAuthChangeTemporaryPassword()
  const changePassword = useAuthChangePassword()
  const updateUserAttributes = useAuthUpdateUserAttributes()

  const loading =
    session.isLoading ||
    userAttributes.isLoading ||
    login.isPending ||
    forgotPassword.isPending ||
    resetPassword.isPending
  const loadingInitial = session.isPending || userAttributes.isPending

  const accessToken = session.data?.tokens?.accessToken.toString() || ''
  const isAuthenticated = !!accessToken
  const isAuthenticatedPreviousValue = usePreviousValue(isAuthenticated)

  const isFirstTimeLogin =
    login.data?.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'

  const payload = session.data?.tokens?.accessToken.payload
  const cognitoGroups = payload?.['cognito:groups']
  const hasGroups = Array.isArray(cognitoGroups) && cognitoGroups.length > 0
  const isImpersonating = hasGroups && cognitoGroups.length > 1
  const isMarqiiUser = !!userAttributes.data?.email?.includes('@marqii.com')
  const accountId = hasGroups ? (cognitoGroups[0] as string) : ''
  const userId = userAttributes.data?.sub || ''

  const originalAccountId = !isFirstTimeLogin ? userAttributes.data?.['custom:tenantId'] : ''

  useEffect(() => {
    if (!isAuthenticated) return

    const authPathsToList = Object.values(PATHS)
      .filter((path) => path.includes('/auth'))
      .map((p) => String(p))

    if (redirectUri) {
      void navigate(decodeURIComponent(redirectUri), { replace: true })
    } else if (authPathsToList.includes(location.pathname)) {
      void navigate(PATHS.homePage, { replace: true })
    }
    // only redirect when the isAuthenticated changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    const properties = {
      accountId,
      isMarqiiUser: String(isMarqiiUser),
      ...(accountCreatedAt ? { accountCreatedAt: accountCreatedAt.getTime().toString() } : {}),
      ...(accountType ? { accountType } : {}),
    }

    void updateContext({ properties, userId })

    if (userId && accountId) {
      Heap.identify({
        userId,
        currentAccountId: accountId,
        originalAccountId: originalAccountId || null,
        isMarqiiUser,
        isImpersonating,
      })
      NewRelic.identify({
        userId,
        currentAccountId: accountId,
        originalAccountId: originalAccountId || null,
        isMarqiiUser,
        isImpersonating,
      })
    }

    if (userId && !accountId) {
      Heap.track('logged_user_without_account_id', {
        userId,
        originalAccountId: originalAccountId || null,
      })
      NewRelic.addPageAction('logged-user-without-account-id', {
        userId,
        originalAccountId: originalAccountId || null,
      })
    }

    if (!window.newrelic && import.meta.env.PROD) {
      console.warn('New Relic not initialized.')
    }
  }, [
    accountId,
    originalAccountId,
    userId,
    accountCreatedAt,
    accountType,
    isMarqiiUser,
    isImpersonating,
    updateContext,
  ])

  const getAccessToken = useCallback(async () => {
    const { data } = await session.refetch()
    return data?.tokens?.accessToken.toString()
  }, [session])

  // Make the provider update only when it should.
  // We only want to force re-renders if the user,
  // loading or error states change.
  const memoedValue: CognitoAuthContextType = useMemo(
    () => ({
      accessToken,
      accountId,
      isAuthenticated,
      isAuthenticatedPreviousValue,
      isFirstTimeLogin,
      isMarqiiUser,
      loading,
      userId,
      userAttributes: userAttributes.data,
      login: login.mutate,
      logout: logout.mutate,
      changePassword: changePassword.mutateAsync,
      updateUserAttributes: updateUserAttributes.mutateAsync,
      forgotPassword: forgotPassword.mutateAsync,
      resetPassword: resetPassword.mutateAsync,
      changeTemporaryPassword: changeTemporaryPassword.mutateAsync,
      impersonation: {
        isImpersonating,
      },
      getAccessToken,
    }),
    // ignoring exhaustive deps to only re-render children if 'important' state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      accessToken,
      accountId,
      isAuthenticated,
      isFirstTimeLogin,
      isMarqiiUser,
      loading,
      userId,
      userAttributes.data,
    ],
  )

  // We only want to render the underlying app after we
  // assert for the presence of a current user.
  return (
    <CognitoAuthContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </CognitoAuthContext.Provider>
  )
}

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export function useCognitoAuth() {
  return useContext(CognitoAuthContext)
}
